import axiosInstance from '../../../config/axios';

async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error: any) {
      const isServerError = error?.response?.status >= 500;
      const shouldRetry = isServerError && attempt < maxRetries;

      if (!shouldRetry) {
        throw error;
      }

      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, Math.min(delay, 8000)));
    }
  }

  throw new Error('Failed after all retries');
}

export interface AttachmentVariable {
  key: string;
  label: string;
  type: 'string' | 'number' | 'decimal' | 'date' | 'select' | 'checkbox' | 'boolean';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  help_text?: string;
}

export interface AttachmentTemplate {
  template_id: number;
  template_name: string;
  template_key: string;
  category: 'recommended' | 'optional';
  default_selected?: boolean;
  description?: string;
  variables_schema?: AttachmentVariable[];
  order_index?: number;
}

export interface AttachmentSelection {
  template_id: number;
  order_index: number;
  variables?: Record<string, any>;
}

export interface BundleItem {
  id: number;
  template_id: number;
  template_name: string;
  item_type: 'MAIN' | 'ANNEX';
  order_index: number;
  pdf_url?: string;
  preview_url?: string;
}

export interface Bundle {
  id: number;
  document_id: number;
  items: BundleItem[];
  combined_pdf_url?: string;
  cover_page_url?: string;
  suggested_file_name?: string;
  created_at: string;
}

interface BackendAttachment {
  template_id: number;
  template_key: string;
  template_code: string;
  label: string;
  category: string;
  default_selected: boolean;
  order_index?: number;
  description?: string;
  variables_schema?: AttachmentVariable[];
}

interface BackendAttachmentsResponse {
  attachments: BackendAttachment[];
  work_area: {
    id: number;
    name: string;
  };
  position_key: string | null;
}

export interface AttachmentsResponse {
  attachments: AttachmentTemplate[];
  work_area: {
    id: number;
    name: string;
  };
  position_key: string | null;
}

export async function fetchAttachments(
  workAreaId: number,
  positionKey?: string | null
): Promise<AttachmentsResponse> {
  const params: any = {};
  if (positionKey) {
    params.position_key = positionKey;
  }

  return fetchWithRetry(async () => {
    const { data } = await axiosInstance.get<BackendAttachmentsResponse>(
      `/api/admin/work-areas/${workAreaId}/attachments/`,
      { params }
    );

    // Transform backend response to frontend format
    const transformedAttachments: AttachmentTemplate[] = data.attachments.map(att => ({
      template_id: att.template_id,
      template_name: att.label,
      template_key: att.template_key,
      category: att.default_selected ? 'recommended' : 'optional',
      default_selected: att.default_selected,
      description: att.description,
      variables_schema: att.variables_schema,
      order_index: att.order_index,
    }));

    return {
      attachments: transformedAttachments,
      work_area: data.work_area,
      position_key: data.position_key,
    };
  });
}
